import { useEffect, useId, useRef, useState } from 'react';

/** Largo minimo del motivo. "ok" o "." no explican nada a quien lea la auditoria después. */
const MINIMO = 10;

/**
 * Pide un motivo escrito antes de dejar ejecutar una accion.
 *
 * El motivo NO es un formalismo: CA-10.2 exige que toda accion mutante quede
 * auditada con quien, que, cuando y sobre que, y CA-10.3 lo pide explicito para
 * los parametros de dispatch. El caso que justifica la friccion es concreto:
 * meses despues alguien mira por que este conductor esta suspendido, o por que
 * el reparto cambio esa semana, y la unica respuesta posible es la que se
 * escribio acá.
 *
 * Por eso el boton de confirmar arranca deshabilitado y solo se habilita con el
 * motivo escrito. No hay forma de saltearlo desde la interfaz.
 */
export function DialogoMotivo({
  titulo,
  descripcion,
  etiquetaConfirmar,
  peligroso = false,
  alConfirmar,
  alCancelar,
}: {
  titulo: string;
  descripcion: string;
  etiquetaConfirmar: string;
  peligroso?: boolean | undefined;
  alConfirmar: (motivo: string) => void;
  alCancelar: () => void;
}) {
  const [motivo, setMotivo] = useState('');
  const areaRef = useRef<HTMLTextAreaElement>(null);
  const idTitulo = useId();
  const idAyuda = useId();

  const suficiente = motivo.trim().length >= MINIMO;

  useEffect(() => {
    areaRef.current?.focus();
    const alTeclado = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') alCancelar();
    };
    document.addEventListener('keydown', alTeclado);
    return () => document.removeEventListener('keydown', alTeclado);
  }, [alCancelar]);

  return (
    <div
      className="velo"
      onMouseDown={(evento) => {
        if (evento.target === evento.currentTarget) alCancelar();
      }}
    >
      <div className="dialogo" role="dialog" aria-modal="true" aria-labelledby={idTitulo}>
        <div className="dialogo__cabecera">
          <p className="rotulo">Motivo obligatorio</p>
          <h2 className="bloque__titulo" id={idTitulo} style={{ marginTop: 4 }}>
            {titulo}
          </h2>
        </div>

        <div className="dialogo__cuerpo">
          <p className="pagina__ayuda" style={{ marginTop: 0, marginBottom: 12 }}>
            {descripcion}
          </p>

          <label className="campo">
            <span className="campo__etiqueta rotulo">Por qué</span>
            <textarea
              ref={areaRef}
              className="area"
              value={motivo}
              onChange={(evento) => setMotivo(evento.target.value)}
              aria-describedby={idAyuda}
              placeholder="Escribí qué pasó y por qué esta acción corresponde."
            />
            <span className="campo__ayuda" id={idAyuda}>
              Queda en la auditoría con tu nombre y la hora de servidor. Lo va a leer quien revise
              esto después. Mínimo {MINIMO} caracteres.
            </span>
          </label>
        </div>

        <div className="dialogo__acciones">
          <button type="button" className="boton" onClick={alCancelar}>
            Cancelar
          </button>
          <button
            type="button"
            className={`boton ${peligroso ? 'boton--peligro' : 'boton--principal'}`}
            disabled={!suficiente}
            onClick={() => alConfirmar(motivo.trim())}
          >
            {etiquetaConfirmar}
          </button>
        </div>
      </div>
    </div>
  );
}
